import { Button, Tooltip } from '@material-ui/core';
import { faCopy, faRotate } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState } from 'react';
import styled from 'styled-components';
import { PublishGate, PublishGateCheck, PublishGateStatus } from 'services/health';

type IProps = {
  gate: PublishGate | null;
  loading: boolean;
  persisting: boolean;
  onRefresh: () => void;
  // Present only on the Health screen: runs every check server-side and stores the verdict.
  onPersist?: () => void;
};

const STATUS_COLOR: Record<PublishGateStatus, string> = {
  green: '#15803d',
  warning: '#f59e0b',
  red: '#b91c1c',
  'no-run': '#4b5563',
};

const STATUS_LABEL: Record<PublishGateStatus, string> = {
  green: 'Publishable',
  warning: 'Publishable · warnings',
  red: 'Blocked',
  'no-run': 'Not verified',
};

const copyIds = (check: PublishGateCheck) => {
  void navigator.clipboard?.writeText(check.pendingIds.join(','));
};

const CheckLine: React.FC<{ check: PublishGateCheck; tone: string }> = ({ check, tone }) => (
  <CheckRow>
    <CheckId $tone={tone}>{check.checkId}</CheckId>
    <CheckPercent>{check.completionPercent.toFixed(1)}%</CheckPercent>
    {check.pendingIds.length > 0 && (
      <Tooltip title={`Copy ${check.pendingIds.length} pending ids`}>
        <CopyButton onClick={() => copyIds(check)}>
          {(check.pendingCount ?? check.pendingIds.length) > check.pendingIds.length
            ? `${check.pendingIds.length} of ${check.pendingCount} ids`
            : `${check.pendingIds.length} ids`}{' '}
          <FontAwesomeIcon icon={faCopy} />
        </CopyButton>
      </Tooltip>
    )}
    {check.note && <ExceptNote>{check.note}</ExceptNote>}
  </CheckRow>
);

const PublishGateView: React.FC<IProps> = ({ gate, loading, persisting, onRefresh, onPersist }) => {
  const [expanded, setExpanded] = useState(false);

  const status: PublishGateStatus = gate?.status ?? 'no-run';
  const attention = gate?.resources.filter((resource) => resource.status !== 'green' || resource.excepted.length > 0) ?? [];
  const greens = (gate?.resources.length ?? 0) - attention.length;

  return (
    <GateContainer>
      <GateHeader>
        <StatusDot $color={STATUS_COLOR[status]} />
        <GateTitle>Publish gate</GateTitle>
        <StatusText $color={STATUS_COLOR[status]}>{loading ? 'Loading…' : STATUS_LABEL[status]}</StatusText>
        {gate?.run && (
          <RunInfo>
            run #{gate.run.runId} · {gate.run.createdAt} · {gate.run.totalChecks} checks
            {gate.run.withIssues > 0 ? ` · ${gate.run.withIssues} with issues` : ''}
          </RunInfo>
        )}
        {!loading && !gate?.run && <RunInfo>No stored verification run for this course.</RunInfo>}
        <GateActions>
          <Button size="small" variant="outlined" onClick={onRefresh} disabled={loading || persisting}>
            <FontAwesomeIcon icon={faRotate} spin={loading} />
          </Button>
          {onPersist && (
            <Button size="small" variant="contained" color="primary" onClick={onPersist} disabled={persisting || loading}>
              {persisting ? 'Verifying…' : 'Verify & save for publish'}
            </Button>
          )}
        </GateActions>
      </GateHeader>

      {!loading && gate && attention.length > 0 && (
        <>
          <ToggleDetail onClick={() => setExpanded(!expanded)}>
            {attention.length} resource{attention.length === 1 ? '' : 's'} need attention
            {greens > 0 ? ` · ${greens} green` : ''} {expanded ? '▴' : '▾'}
          </ToggleDetail>
          {expanded && (
            <ResourceList>
              {attention.map((resource) => (
                <ResourceRow key={resource.ref}>
                  <ResourceHeader>
                    <StatusDot $color={STATUS_COLOR[resource.status]} $small />
                    <ResourceName>{resource.name}</ResourceName>
                  </ResourceHeader>
                  {resource.blockers.map((check) => (
                    <CheckLine key={check.checkId} check={check} tone={STATUS_COLOR.red} />
                  ))}
                  {resource.warnings.map((check) => (
                    <CheckLine key={check.checkId} check={check} tone={STATUS_COLOR.warning} />
                  ))}
                  {resource.excepted.map((check) => (
                    <CheckLine key={check.checkId} check={check} tone={STATUS_COLOR['no-run']} />
                  ))}
                </ResourceRow>
              ))}
            </ResourceList>
          )}
        </>
      )}
      {!loading && gate && attention.length === 0 && status === 'green' && (
        <AllGreen>All {gate.resources.length} resources green.</AllGreen>
      )}
    </GateContainer>
  );
};

export default PublishGateView;

const GateContainer = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background-color: #fff;
  padding: 10px 14px;
  margin-bottom: 12px;
  width: 100%;
`;

const GateHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const StatusDot = styled.span<{ $color: string; $small?: boolean }>`
  width: ${({ $small }) => ($small ? '10px' : '14px')};
  height: ${({ $small }) => ($small ? '10px' : '14px')};
  border-radius: 50%;
  background-color: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const GateTitle = styled.span`
  font-weight: 600;
  color: #1f2937;
`;

const StatusText = styled.span<{ $color: string }>`
  font-weight: 600;
  color: ${({ $color }) => $color};
`;

const RunInfo = styled.span`
  font-size: 12px;
  color: #6b7280;
`;

const GateActions = styled.div`
  margin-left: auto;
  display: flex;
  gap: 8px;
`;

const ToggleDetail = styled.div`
  margin-top: 8px;
  font-size: 13px;
  color: #1d4ed8;
  cursor: pointer;
  user-select: none;
`;

const AllGreen = styled.div`
  margin-top: 8px;
  font-size: 13px;
  color: #15803d;
`;

const ResourceList = styled.div`
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ResourceRow = styled.div`
  border-left: 3px solid #e5e7eb;
  padding-left: 10px;
`;

const ResourceHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ResourceName = styled.span`
  font-weight: 600;
  font-size: 13px;
  color: #1f2937;
`;

const CheckRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
  padding: 2px 0 2px 18px;
  flex-wrap: wrap;
`;

const CheckId = styled.span<{ $tone: string }>`
  font-family: monospace;
  color: ${({ $tone }) => $tone};
`;

const CheckPercent = styled.span`
  color: #4b5563;
`;

const CopyButton = styled.span`
  color: #1d4ed8;
  cursor: pointer;
  user-select: none;
`;

const ExceptNote = styled.span`
  color: #6b7280;
  font-style: italic;
`;
